import React from "react";
import { useHistory } from "react-router-dom";
import { WSButton, WSElement, WSText } from "@wingspanhq/fe-component-library";
import {
  SubscriptionPackage,
  SubscriptionPackageTier
} from "@wingspanhq/users/dist/lib/interfaces";
import MeridioHealthInsuranceImage from "../../../assets/MeridioHorizontalLogo.png";
import MeridioHealthInsuranceLogo from "../../../assets/MeridioSmallLogo.png";

import {
  IWebflowBenefitCategory,
  IWebflowBenefitMinimumSubscription
} from "@wingspanhq/cms/dist/lib/interfaces";
import qs from "qs";
import { ExtendedWebflowBenefit } from "../../../types";
import { useUserId } from "../../../../../query/hooks/helpers";
import { useQueryMemberProfile } from "../../../../../query/users/queries";
import { openInNewTab } from "../../../../../shared/utils/openInNewTab";
import { MERIDIO_KEY } from "../../../utils/constants";
import { useFeatureFlags } from "../../../../../query/hooks/useFeatureFlags";
import { PATH_BENEFITS_MERIDIO_EXPLORE_PLANS } from "../../../paths";

const MeridioCTA: React.FC<{}> = ({}) => {
  const history = useHistory();
  const userId = useUserId();
  const queryFeatureFlags = useFeatureFlags();

  const qMember = useQueryMemberProfile(userId);

  const member = qMember.data;

  const searchParams = member
    ? qs.stringify({
        name: {
          first: member.user.profile.firstName,
          last: member.user.profile.lastName
        },
        phoneNumber: member.user.phone.number?.substring(2),
        email: member.user.email,
        wingspanId: member.memberId
      })
    : "";

  return (
    <WSButton.Primary
      fullWidth
      shimmer={qMember.isLoading || queryFeatureFlags.isLoading}
      onClick={() => {
        if (!queryFeatureFlags.data?.meridioInAppEnrollment) {
          openInNewTab(`https://bnfts.us/wingspan-onboard?${searchParams}`);
        } else {
          history.push(PATH_BENEFITS_MERIDIO_EXPLORE_PLANS);
        }
      }}
    >
      {queryFeatureFlags.data?.meridioInAppEnrollment
        ? "Explore plans"
        : "Get Started!"}
    </WSButton.Primary>
  );
};

export const getMeridioHealthInsuranceBenefit = () => {
  const benefit: ExtendedWebflowBenefit = {
    helperLogo: {
      src: MeridioHealthInsuranceImage,
      placeholder: ""
    },
    logo: {
      src: MeridioHealthInsuranceLogo,
      placeholder: ""
    },
    mainVideo: {
      src: "https://player.vimeo.com/video/827040201?h=ae3c844ace"
    },
    collectionId: "",
    editedOn: new Date(),
    isDraft: false,
    name: "Health Insurance",
    itemId: "",
    publishedOn: new Date(),
    slug: MERIDIO_KEY,
    category: IWebflowBenefitCategory.Insurance,
    minSubscription: IWebflowBenefitMinimumSubscription.Benefits,
    minSubscriptionPackage: SubscriptionPackage.Benefits,
    minSubscriptionPackageTier: SubscriptionPackageTier.Basic,
    atAGlance:
      "Where health benefits meet everyday people. Telehealth available 24/7.",
    featured: true,
    helperCostDescription: "Plans start as low as $170/month",
    helperCostIsNotIncluded: true,
    helperCTACustomButton: <MeridioCTA />,
    helperHeader: "Helping you Work, Plan and Live Better.",
    helperHideMemberAndGroupInfo: true,
    helperUsageText:
      "Review medical, dental & vision plans and schedule a needs assessment with a licensed Benefits Guide.",
    isAdditionalCoverage: true,
    hidden: false,
    mainCards:
      "heart\nPreventive Care and Competitive Health Coverage\n-\nhealth\nDental & Vision Coverage",
    // "health\nComprehensive\nHealth\nInsurance\n-\ndental\nDental &\nVision\nCoverage\n-\nbankFees\nBudget-friendly\nOptions\n ",
    mainDescription:
      "Wingspan partners with Meridio to offer contractors access to healthcare benefits and assistance in navigating options from preventive to traditional medical, dental & vision coverage.",
    mainFAQSItems: (
      <WSText.ParagraphSm as="div">
        <WSText.Paragraph mb="M" weight="medium">
          Am I still eligible for benefits if I have not worked any hours?
        </WSText.Paragraph>
        Yes! Wingspan has partnered with Meridio to provide access to all
        contractors. Ensure that you have a default payment method by going to:
        <WSElement as="ul" ml="XL">
          <li>Settings</li>

          <li>Payout & Payment Methods</li>

          <li>Add payment method</li>

          <li>Mark as the default payment method</li>
        </WSElement>
        <WSText.Paragraph mt="L" mb="M" weight="medium">
          What kind of health coverage does Meridio offer?
        </WSText.Paragraph>
        Meridio offers preventive and competitive plans starting at $170/month
        per individual. Dental & Vision plans start at $30/month per individual.
        <WSText.Paragraph mt="L" mb="M" weight="medium">
          Why choose Meridio?
        </WSText.Paragraph>
        Meridio’s health plans are designed to support contractors offering
        guaranteed acceptance, standard rates in all 50 states, $0 deductibles,
        and reasonable premiums. With Meridio, you choose the benefits that are
        right for you and your household, and if you change careers, your
        coverage plan goes with you.
        <WSText.Paragraph mt="L" mb="M" weight="medium">
          Where can I review plans?
        </WSText.Paragraph>
        You can review plan options by visiting{" "}
        <a href="https://base.getmeridio.com/b/ws-b?companydisplayname=Wingspan&cd=Q87LLCI6&phone=1-888-294-9181">
          HERE
        </a>
        <WSText.Paragraph mt="L" mb="M" weight="medium">
          Can I add my significant other?
        </WSText.Paragraph>
        Yes, you can add your significant other, fiance, or domestic partner.
        Meridio also provides coverage options for children and family members.
        <WSText.Paragraph mt="L" mb="M" weight="medium">
          Will my physician be in network?
        </WSText.Paragraph>
        Meridio utilizes the PHCS network through Multiplan which is the largest
        independent PPO network in the nation. You can visit{" "}
        <a href="https://www.multiplan.us/">multiplan.com</a> to search for your
        existing providers following the steps below:
        <WSElement as="ul" ml="XL">
          <li>Click “Find a Provider” located at the top right-hand corner</li>
          <li>Click “Select Network”</li>
          <li>
            Select “PHCS” Depending on the coverage type your interested in next
            steps will vary: <br />
            <b>Preventive:</b> MEC Ultimate → select “Preventive only” MEC
            Ultimate HI → select “Specifics” <br />
            <b>Competitive:</b> Both IHP plans → select “Practitioner &
            Ancillary”
          </li>
        </WSElement>
        <WSText.Paragraph mt="L" mb="M" weight="medium">
          When is open enrollment?
        </WSText.Paragraph>
        Open enrollment is year-round. If you’re looking for coverage as soon as
        the 1st of the following month, enroll by the 10th of the current month.
        <WSText.Paragraph mt="L" mb="M" weight="medium">
          When do my benefits take effect?
        </WSText.Paragraph>
        Depending on when you enroll, benefits take effect on the first of the
        following month.
        <WSText.Paragraph mt="L" mb="M" weight="medium">
          When will I get my ID cards?
        </WSText.Paragraph>
        Physical cards and packet will be mailed mid-month AFTER your coverage
        start date.
        <WSText.Paragraph mt="L" mb="M" weight="medium">
          ****To access your digital cards:
        </WSText.Paragraph>
        <WSElement as="ul" ml="XL">
          <li>Competitive (IHP) Plans: Refer to the guidebook</li>

          <li>
            Preventive (MEC) Plans: Email request to{" "}
            <a href="mailto:customercare@getmeridio.com">
              customercare@getmeridio.com
            </a>
          </li>
        </WSElement>
      </WSText.ParagraphSm>
    ) as any as string,
    mainHeader: "Everyday Health",
    orderPriority: 0,
    enrolledPlanType: "MeridioHealth" as const
  };

  return benefit;
};
